/**
 * ============================================================
 * Wingman RC5 — Indoor Airport Intelligence
 * MapboxIndoorDiscoveryAdapter.ts
 *
 * Deterministic normalization + deduplication of
 * Mapbox Indoor v3 raw features into AirportPOI objects.
 *
 * IAM-M16B-R1.4B — Airport Discovery Adapter
 * ============================================================
 */

import {
  type AirportPOI,
  AirportCategory,
  type INormalizationAdapter,
} from './AirportDiscoveryContracts';

// ── Category Mapping ─────────────────────────────────────────

const SOURCE_LAYER_TO_CATEGORY: Record<string, AirportCategory> = {
  'indoor-gate': AirportCategory.GATE,
  'indoor-security': AirportCategory.SECURITY,
  'indoor-check-in': AirportCategory.CHECK_IN,
  'indoor-customs': AirportCategory.CUSTOMS,
  'indoor-immigration': AirportCategory.IMMIGRATION,
  'indoor-lounge': AirportCategory.LOUNGE,
  'indoor-restaurant': AirportCategory.RESTAURANT,
  'indoor-retail': AirportCategory.RETAIL,
  'indoor-restroom': AirportCategory.RESTROOM,
  'indoor-baggage-claim': AirportCategory.BAGGAGE_CLAIM,
  'indoor-ground-transport': AirportCategory.GROUND_TRANSPORT,
  'indoor-information': AirportCategory.INFORMATION,
  'indoor-connector': AirportCategory.CONNECTOR,
  'indoor-elevator': AirportCategory.ELEVATOR,
  'indoor-escalator': AirportCategory.ESCALATOR,
  'indoor-stairs': AirportCategory.STAIRS,
  'indoor-corridor': AirportCategory.CORRIDOR,
};

/**
 * Infer AirportCategory from a raw feature's source layer or name.
 */
function inferCategory(rawFeature: Record<string, unknown>): AirportCategory {
  const sourceLayer = String(rawFeature['sourceLayer'] ?? rawFeature['source_layer'] ?? '');
  if (SOURCE_LAYER_TO_CATEGORY[sourceLayer]) {
    return SOURCE_LAYER_TO_CATEGORY[sourceLayer];
  }

  // Fallback: infer from name
  const name = String(rawFeature['name'] ?? '').toLowerCase();
  if (/gate\s*[a-z]?\d/i.test(name)) return AirportCategory.GATE;
  if (/security|tsa/i.test(name)) return AirportCategory.SECURITY;
  if (/check.?in/i.test(name)) return AirportCategory.CHECK_IN;
  if (/lounge/i.test(name)) return AirportCategory.LOUNGE;
  if (/restroom|bathroom|toilet/i.test(name)) return AirportCategory.RESTROOM;
  if (/elevator/i.test(name)) return AirportCategory.ELEVATOR;
  if (/escalator/i.test(name)) return AirportCategory.ESCALATOR;
  if (/stair/i.test(name)) return AirportCategory.STAIRS;
  if (/corridor|hallway|walkway/i.test(name)) return AirportCategory.CORRIDOR;

  return AirportCategory.UNKNOWN;
}

/**
 * Extract coordinates from raw feature geometry.
 * Supports GeoJSON Point, centroid property, or direct lon/lat.
 */
function extractCoordinates(rawFeature: Record<string, unknown>): [number, number] | null {
  // GeoJSON geometry
  const geometry = rawFeature['geometry'] as Record<string, unknown> | undefined;
  if (geometry && Array.isArray(geometry['coordinates'])) {
    const coords = geometry['coordinates'];
    // Point geometry
    if (typeof coords[0] === 'number' && typeof coords[1] === 'number') {
      return [coords[0] as number, coords[1] as number];
    }
    // Polygon/LineString — compute centroid
    if (Array.isArray(coords[0])) {
      return computeCentroid(coords as number[][]);
    }
  }

  // Direct properties
  const lon = Number(rawFeature['longitude'] ?? rawFeature['lon'] ?? rawFeature['lng']);
  const lat = Number(rawFeature['latitude'] ?? rawFeature['lat']);
  if (!isNaN(lon) && !isNaN(lat) && lon !== 0 && lat !== 0) {
    return [lon, lat];
  }

  return null;
}

/**
 * Compute centroid of a coordinate array.
 */
function computeCentroid(coords: number[][]): [number, number] {
  let sumLon = 0;
  let sumLat = 0;
  let count = 0;

  for (const coord of coords) {
    if (Array.isArray(coord) && coord.length >= 2) {
      // Handle nested arrays (polygon rings)
      if (Array.isArray(coord[0])) {
        for (const inner of coord as unknown as number[][]) {
          if (inner.length >= 2) {
            sumLon += inner[0];
            sumLat += inner[1];
            count++;
          }
        }
      } else {
        sumLon += coord[0];
        sumLat += coord[1];
        count++;
      }
    }
  }

  if (count === 0) return [0, 0];
  return [sumLon / count, sumLat / count];
}

/**
 * Generate a stable, deterministic ID for a POI.
 */
function generateStableId(
  name: string,
  category: AirportCategory,
  level: number,
  coordinates: [number, number],
): string {
  const coordKey = `${coordinates[0].toFixed(6)}_${coordinates[1].toFixed(6)}`;
  const nameKey = name.toLowerCase().replace(/[^a-z0-9]/g, '_');
  return `${category}-${nameKey}-L${level}-${coordKey}`;
}

// ── Adapter Implementation ───────────────────────────────────

export class MapboxIndoorDiscoveryAdapter implements INormalizationAdapter {
  private readonly terminal: string;

  constructor(terminal: string = 'T4') {
    this.terminal = terminal;
  }

  /**
   * Normalize a raw Mapbox Indoor v3 feature into an AirportPOI.
   * Returns null if the feature cannot be normalized (missing coords, etc.).
   */
  normalize(rawFeature: Record<string, unknown>): AirportPOI | null {
    const coordinates = extractCoordinates(rawFeature);
    if (!coordinates) return null;

    const name = String(rawFeature['name'] ?? rawFeature['label'] ?? 'Unknown');
    const category = inferCategory(rawFeature);
    const level = Number(rawFeature['level'] ?? rawFeature['floor'] ?? rawFeature['floor_id'] ?? 0);

    const sourceLayer = String(rawFeature['sourceLayer'] ?? rawFeature['source_layer'] ?? '');
    const concourse = String(rawFeature['concourse'] ?? '');

    const id = generateStableId(name, category, level, coordinates);

    return {
      id,
      name,
      category,
      level,
      coordinates: [coordinates[0], coordinates[1]] as const,
      terminal: this.terminal,
      concourse: concourse || undefined,
      sourceLayer: sourceLayer || undefined,
      metadata: rawFeature,
    };
  }

  /**
   * Deduplicate POIs by stable ID, keeping the first occurrence.
   */
  deduplicate(pois: readonly AirportPOI[]): readonly AirportPOI[] {
    const seen = new Map<string, AirportPOI>();
    for (const poi of pois) {
      if (!seen.has(poi.id)) {
        seen.set(poi.id, poi);
      }
    }
    return Array.from(seen.values());
  }
}
